import { useFileUpload } from '@/hooks/useFileUpload'
import {ImagePlus, Loader2, X} from 'lucide-react'
import { useRef } from 'react'
import toast from 'react-hot-toast'

interface ImageUploadBoxProps {
  value?: string
  onChange: (url: string) => void
  label?: string
  shape?: 'square' | 'wide' | 'round' | 'hero'
  hint?: string
  className?: string
}

const MAX_SIZE_MB = 10

function ImageUploadBox({ value, onChange, label, hint, shape = 'square', className = '' }: ImageUploadBoxProps) {
  const { uploadImage, uploading } = useFileUpload()
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = async (file: File | null) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.')
      return
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_SIZE_MB}MB.`)
      return
    }
    try {
      const url = await uploadImage(file)
      onChange(url)
      toast.success('Image uploaded')
    } catch {
      toast.error('Upload failed. Please try again.')
    }
  }

  const shapeClass
    = shape === 'wide' ? 'aspect-[16/5]'
      : shape === 'hero' ? 'aspect-[16/9]'
        : shape === 'round' ? 'aspect-square rounded-full'
          : 'aspect-square'

  return (
    <div className={className}>
      {label ? <p className="mb-1.5 text-xs font-semibold text-white/60">{label}</p> : null}
      {hint ? <p className="mb-1.5 text-[11px] font-semibold leading-relaxed text-white/40">{hint}</p> : null}
      <div
        className={`relative w-full ${shapeClass} overflow-hidden ${shape === 'round' ? '' : 'rounded-2xl'} border-2 border-dashed border-[#FFDE00]/30 bg-white/[0.03] transition-colors hover:border-[#FFDE00]`}
      >
        {value ? (
          <img src={value} alt="Uploaded" className="h-full w-full object-cover" />
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => handleFile(e.target.files?.[0] || null)}
        />

        {!value && !uploading ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-white/40 hover:text-[#FFDE00]"
          >
            <ImagePlus className="h-6 w-6" />
            <span className="text-xs font-semibold">Click to upload</span>
          </button>
        ) : null}

        {uploading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <Loader2 className="h-5 w-5 animate-spin text-[#FFDE00]" />
          </div>
        ) : null}

        {value && !uploading ? (
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 transition-opacity hover:bg-black/50 hover:opacity-100">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="rounded-full bg-white/10 px-2.5 py-1.5 text-xs font-extrabold text-white hover:bg-white/20"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              className="rounded-full bg-[#FF1A1A]/80 p-1.5 text-white hover:bg-[#FF1A1A]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default ImageUploadBox
